<template>
  <form @submit.prevent="handleSubmit" class="scholar-form">
    <div class="form-grid">
      <div class="form-field span-2">
        <label>{{ $t('scholar.name') }} *</label>
        <IntercomInput v-model="formData.name" required :disabled="loading" />
      </div>
      <div class="form-field">
        <label>{{ $t('scholar.phone') }}</label>
        <IntercomPhoneInput v-model="formData.phone" :disabled="loading" :error="phoneError" @valid="(v) => (phoneValid = v)" />
      </div>
      <div class="form-field">
        <label>{{ $t('scholar.email') }}</label>
        <IntercomEmailInput v-model="formData.email" :disabled="loading" />
      </div>
      <div class="form-field">
        <label>{{ $t('scholar.institution') }}</label>
        <IntercomInput v-model="formData.institution" :disabled="loading" />
      </div>
      <div class="form-field">
        <label>{{ $t('scholar.qualifications') }}</label>
        <IntercomInput v-model="formData.qualifications" :disabled="loading" />
      </div>
      <div class="form-field span-2">
        <label>{{ $t('scholar.specialization') }} *</label>
        <select v-model="formData.school_specialization" class="select-input" required :disabled="loading">
          <option value="hanafi">{{ $t('calculator.hanafi') }}</option>
          <option value="shia_ithna_ashari">{{ $t('calculator.shiaIthnaAshari') }}</option>
        </select>
      </div>
    </div>

    <div class="form-actions">
      <IntercomButton variant="secondary" type="button" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" type="submit" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { IntercomButton, IntercomInput, IntercomPhoneInput, IntercomEmailInput } from '@shared/components/intercom'

const props = defineProps({
  scholar: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const phoneError = ref('')
const phoneValid = ref(true) // optional field

const formData = reactive({
  name: '',
  phone: '',
  email: '',
  institution: '',
  qualifications: '',
  school_specialization: 'hanafi',
})

const handleSubmit = () => {
  if (formData.phone && !phoneValid.value) {
    phoneError.value = 'Enter a valid 10-digit phone number'
    return
  }
  emit('submit', formData)
}

// Populate form when editing
watch(
  () => props.scholar,
  (s) => {
    if (s) {
      formData.name = s.name || ''
      formData.phone = s.phone || ''
      formData.email = s.email || ''
      formData.institution = s.institution || ''
      formData.qualifications = s.qualifications || ''
      formData.school_specialization = s.school_specialization || 'hanafi'
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.scholar-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.form-field.span-2 {
  grid-column: span 2;
}

.form-field label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.select-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
}

.field-error {
  color: var(--color-error);
  font-size: var(--font-size-xs);
  margin-top: var(--space-1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}
</style>
